import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { ProfileAvatarSection } from '@/components/ProfileAvatarSection';
import { useAuth } from '@/hooks/useAuth';
import { useTheme, ColorTheme, DarkMode } from '@/hooks/useTheme';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { User, Trash2, LogOut, Shield, Palette, Sun, Moon, Monitor, BarChart3, Lock } from 'lucide-react';
import { Link } from 'react-router-dom';

interface UserStats {
  approvedWords: number;
  forumTopics: number;
  forumReplies: number;
}

export default function Account() {
  const navigate = useNavigate();
  const { user, session, profile, signOut, deleteAccount, updateProfile, loading, isAuthenticated, isAdmin, isModerator, subscriptionTier } = useAuth();
  const isPaying = subscriptionTier === 'basic' || subscriptionTier === 'premium' || isAdmin;
  const { colorTheme, darkMode, setColorTheme, setDarkMode } = useTheme();
  const [displayName, setDisplayName] = useState('');
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [stats, setStats] = useState<UserStats>({ approvedWords: 0, forumTopics: 0, forumReplies: 0 });
  const [avatarKey, setAvatarKey] = useState(0);

  const handleAvatarChange = useCallback(() => {
    setAvatarKey(prev => prev + 1);
  }, []);

  useEffect(() => {
    if (user?.displayName) {
      setDisplayName(user.displayName);
    }
  }, [user]);

  useEffect(() => {
    const fetchStats = async () => {
      if (!session?.user?.id) return;
      
      const userId = session.user.id;
      
      // Fetch approved word suggestions count
      const { count: approvedWords } = await supabase
        .from('word_suggestions')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('status', 'approved');
      
      // Fetch forum topics count
      const { count: forumTopics } = await supabase
        .from('forum_topics')
        .select('*', { count: 'exact', head: true })
        .eq('author_id', userId);
      
      // Fetch forum replies count
      const { count: forumReplies } = await supabase
        .from('forum_replies')
        .select('*', { count: 'exact', head: true })
        .eq('author_id', userId);
      
      setStats({
        approvedWords: approvedWords || 0,
        forumTopics: forumTopics || 0,
        forumReplies: forumReplies || 0,
      });
    };
    
    fetchStats();
  }, [session?.user?.id]);

  useEffect(() => {
    if (!loading && !session) {
      navigate('/auth');
    }
  }, [loading, session, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Header />
        <main className="flex-1 container mx-auto px-4 py-8 flex items-center justify-center">
          <p>Laddar...</p>
        </main>
        <Footer />
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!displayName.trim() || displayName.length < 2) {
      toast.error('Visningsnamnet måste vara minst 2 tecken');
      return;
    }

    setUpdating(true);
    const { error } = await updateProfile(displayName.trim());

    if (error) {
      toast.error(error);
    } else {
      toast.success('Profil uppdaterad!');
    }
    setUpdating(false);
  };

  const handleSignOut = async () => {
    await signOut();
    toast.success('Du har loggats ut');
    navigate('/');
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    const { error } = await deleteAccount();
    
    if (error) {
      toast.error(error);
      setDeleting(false);
    } else {
      toast.success('Ditt konto har raderats');
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />

      <main className="flex-1 container mx-auto px-4 py-8 max-w-lg">
        <div className="space-y-6">
          {/* Profile Card */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                  <User className="h-5 w-5 text-primary" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <CardTitle className="font-display">Min profil</CardTitle>
                    {isAdmin && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-primary/10 text-primary border border-primary/20">
                        <Shield className="h-3 w-3" />
                        Admin
                      </span>
                    )}
                    {isModerator && !isAdmin && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-accent/50 text-accent-foreground border border-accent">
                        <Shield className="h-3 w-3" />
                        Moderator
                      </span>
                    )}
                  </div>
                  <CardDescription>{user.email}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleUpdateProfile} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="displayName">Visningsnamn</Label>
                  <Input
                    id="displayName"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="Ditt namn i forumet"
                  />
                </div>
                <Button type="submit" disabled={updating}>
                  {updating ? 'Sparar...' : 'Spara ändringar'}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Profile Avatar */}
          {session?.user?.id && (
            <ProfileAvatarSection
              key={avatarKey}
              userId={session.user.id}
              currentAvatarUrl={profile?.avatar_url || null}
              currentDefaultAvatar={profile?.default_avatar ?? null}
              onAvatarChange={handleAvatarChange}
            />
          )}

          {/* User Statistics */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-primary" />
                <CardTitle className="font-display text-lg">Min statistik</CardTitle>
              </div>
              <CardDescription>Dina bidrag till sajten</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="p-3 rounded-lg bg-secondary/40">
                  <p className="text-2xl font-bold text-primary">{stats.approvedWords}</p>
                  <p className="text-xs text-muted-foreground">Godkända ord</p>
                </div>
                <div className="p-3 rounded-lg bg-secondary/40">
                  <p className="text-2xl font-bold text-primary">{stats.forumTopics}</p>
                  <p className="text-xs text-muted-foreground">Forumtrådar</p>
                </div>
                <div className="p-3 rounded-lg bg-secondary/40">
                  <p className="text-2xl font-bold text-primary">{stats.forumReplies}</p>
                  <p className="text-xs text-muted-foreground">Forumsvar</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Theme Settings */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Palette className="h-5 w-5 text-primary" />
                <CardTitle className="font-display text-lg">Utseende</CardTitle>
                {!isPaying && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-muted text-muted-foreground">
                    <Lock className="h-3 w-3" />
                    Premium
                  </span>
                )}
              </div>
              <CardDescription>
                {isPaying 
                  ? 'Välj färgtema och ljusläge'
                  : 'Uppgradera till Basic eller Premium för att anpassa utseendet'
                }
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Color Theme */}
              <div className="space-y-3">
                <Label className={!isPaying ? 'text-muted-foreground' : ''}>Färgtema</Label>
                <RadioGroup 
                  value={colorTheme} 
                  onValueChange={(value) => setColorTheme(value as ColorTheme)}
                  className="grid grid-cols-2 gap-3"
                  disabled={!isPaying}
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="green" id="theme-green" disabled={!isPaying} />
                    <Label htmlFor="theme-green" className={`flex items-center gap-2 ${!isPaying ? 'cursor-not-allowed text-muted-foreground' : 'cursor-pointer'}`}>
                      <span className={`w-4 h-4 rounded-full bg-[hsl(75,45%,32%)] ${!isPaying ? 'opacity-50' : ''}`} />
                      Olivgrön
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="warm" id="theme-warm" disabled={!isPaying} />
                    <Label htmlFor="theme-warm" className={`flex items-center gap-2 ${!isPaying ? 'cursor-not-allowed text-muted-foreground' : 'cursor-pointer'}`}>
                      <span className={`w-4 h-4 rounded-full bg-[hsl(25,55%,42%)] ${!isPaying ? 'opacity-50' : ''}`} />
                      Terrakotta
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="blue" id="theme-blue" disabled={!isPaying} />
                    <Label htmlFor="theme-blue" className={`flex items-center gap-2 ${!isPaying ? 'cursor-not-allowed text-muted-foreground' : 'cursor-pointer'}`}>
                      <span className={`w-4 h-4 rounded-full bg-[hsl(210,65%,45%)] ${!isPaying ? 'opacity-50' : ''}`} />
                      Havsblå
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="purple" id="theme-purple" disabled={!isPaying} />
                    <Label htmlFor="theme-purple" className={`flex items-center gap-2 ${!isPaying ? 'cursor-not-allowed text-muted-foreground' : 'cursor-pointer'}`}>
                      <span className={`w-4 h-4 rounded-full bg-[hsl(270,55%,50%)] ${!isPaying ? 'opacity-50' : ''}`} />
                      Lavendel
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="forest" id="theme-forest" disabled={!isPaying} />
                    <Label htmlFor="theme-forest" className={`flex items-center gap-2 ${!isPaying ? 'cursor-not-allowed text-muted-foreground' : 'cursor-pointer'}`}>
                      <span className={`w-4 h-4 rounded-full bg-[hsl(150,45%,35%)] ${!isPaying ? 'opacity-50' : ''}`} />
                      Skogsgrön
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Dark Mode */}
              <div className="space-y-3">
                <Label className={!isPaying ? 'text-muted-foreground' : ''}>Ljusläge</Label>
                <RadioGroup 
                  value={darkMode} 
                  onValueChange={(value) => setDarkMode(value as DarkMode)}
                  className="flex gap-4"
                  disabled={!isPaying}
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="light" id="mode-light" disabled={!isPaying} />
                    <Label htmlFor="mode-light" className={`flex items-center gap-2 ${!isPaying ? 'cursor-not-allowed text-muted-foreground' : 'cursor-pointer'}`}>
                      <Sun className={`h-4 w-4 ${!isPaying ? 'opacity-50' : ''}`} />
                      Ljust
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="dark" id="mode-dark" disabled={!isPaying} />
                    <Label htmlFor="mode-dark" className={`flex items-center gap-2 ${!isPaying ? 'cursor-not-allowed text-muted-foreground' : 'cursor-pointer'}`}>
                      <Moon className={`h-4 w-4 ${!isPaying ? 'opacity-50' : ''}`} />
                      Mörkt
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="system" id="mode-system" disabled={!isPaying} />
                    <Label htmlFor="mode-system" className={`flex items-center gap-2 ${!isPaying ? 'cursor-not-allowed text-muted-foreground' : 'cursor-pointer'}`}>
                      <Monitor className={`h-4 w-4 ${!isPaying ? 'opacity-50' : ''}`} />
                      System
                    </Label>
                  </div>
                </RadioGroup>
              </div>
            </CardContent>
          </Card>

          {/* Logout */}
          <Card>
            <CardHeader>
              <CardTitle className="font-display text-lg">Logga ut</CardTitle>
            </CardHeader>
            <CardContent>
              <Button variant="outline" onClick={handleSignOut} className="gap-2">
                <LogOut className="h-4 w-4" />
                Logga ut
              </Button>
            </CardContent>
          </Card>

          {/* Delete Account */}
          <Card className="border-destructive/50">
            <CardHeader>
              <CardTitle className="font-display text-lg text-destructive">Radera konto</CardTitle>
              <CardDescription>
                Detta raderar permanent ditt konto, alla dina forumtrådar och svar. 
                Åtgärden kan inte ångras.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" className="gap-2">
                    <Trash2 className="h-4 w-4" />
                    Radera mitt konto
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Är du helt säker?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Detta kommer permanent radera ditt konto och alla dina foruminlägg. 
                      Denna åtgärd kan inte ångras.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Avbryt</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleDeleteAccount}
                      disabled={deleting}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      {deleting ? 'Raderar...' : 'Ja, radera allt'}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </CardContent>
          </Card>

          {/* GDPR Info */}
          <Card>
            <CardHeader>
              <CardTitle className="font-display text-lg">Dina uppgifter</CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground space-y-2">
              <p><strong>E-post:</strong> {user.email}</p>
              <p><strong>Visningsnamn:</strong> {user.displayName}</p>
              <p><strong>Konto skapat:</strong> {profile?.created_at ? new Date(profile.created_at).toLocaleDateString('sv-SE') : '-'}</p>
              <p><strong>GDPR-samtycke:</strong> {profile?.gdpr_consent_at ? new Date(profile.gdpr_consent_at).toLocaleDateString('sv-SE') : '-'}</p>
            </CardContent>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
}